import { defineStore } from 'pinia';
import { navigateTo, useNuxtApp } from '#app'; // Needed for Nuxt integration
import { useAvatar } from '~/composables/useAvatar';
import { resetAllStores } from '~/lib/functions/storeControls';

// Shared by every concurrent ensureAvatar() caller, so a burst of them (the
// plugin watcher, a page remount, a route change) does one canvas + upload
// rather than one each. Module scope rather than store state because it holds
// a Promise, which has no business being reactive.
//
// Tagged with the user id it belongs to. Sharing it blindly is only correct
// while the target is the same person: an account switch mid-generation would
// otherwise hand B the promise generating A's avatar, return A's URL, and mark
// nothing for B — who then has no avatar until the next cold start.
let avatarEnsureInFlight = null;
let avatarEnsureInFlightFor = null;

export const useUserStore = defineStore('user', {
  state: () => ({
    user: { id: '', avatar_url: '', is_anonymous: false },
    userProfile: { id: '', avatar_url: '' },
    loading: true,
    isFetching: false,
    isAdmin: false,
    // Which user id we have already settled the avatar question for. Not a
    // boolean: signing out and into a different account must re-run it.
    avatarEnsuredFor: null,
    // Same shape, for the origin-IP stamp. See ensureSessionIp.
    ipEnsuredFor: null,
    // Bumped whenever the set of content this account owns changes underneath
    // pages that are ALREADY mounted — today that means a guest merge, which
    // moves tree_items / cards / diagrams onto a different uid. Pages holding
    // their own fetched copy of that content (the dashboard's get_dashboard_data
    // RPC, for one) watch this and refetch. Watching `user.id` is not a
    // substitute: on the OAuth return the id changes when the session is
    // exchanged, which is BEFORE merge-guest has moved anything, so a refetch
    // keyed to it lands on the pre-merge data and stays there.
    ownedContentRevision: 0,
  }),
  getters: {
    // NOTE: anonymous (guest) users have a real id, so isLoggedIn is true for
    // them too. Use isMember to gate anything a guest must not do.
    isLoggedIn: (state) => !!state.user?.id,
    isGuest: (state) => !!state.user?.id && state.user.is_anonymous === true,
    isMember: (state) => !!state.user?.id && state.user.is_anonymous !== true,
  },
  actions: {
    async initAuth() {
      this.loading = true;
      const { $supabase } = useNuxtApp();

      const {
        data: { session },
      } = await $supabase.auth.getSession();

      if (session?.user) {
        this.user = session.user;
        await this.checkAdmin();
        this.loading = false;
        return;
      }

      // No session. For a plain visitor that is the final answer, NOT a
      // "wait and see": plugins/supabase.js already awaited getSession() before
      // the app rendered, and supabase-js resolves that only after it has
      // finished any PKCE / implicit-flow exchange found in the URL. So a null
      // session here means there is nothing to arrive.
      //
      // This used to fall through to the 5s safety net below with loading still
      // true, and everything gated on `!userStore.loading` — the whole navbar,
      // the hero's guest CTA, GuestStartButton — stayed unrendered for five
      // seconds while the page itself was already painted. Measured on the
      // landing page in an incognito browser: page at 1.7s, navbar at 6.3s.
      //
      // The listener stays as a late-arrival safety net (an auth event from
      // another tab, or an exchange that somehow lost the race), but it must
      // never block the first render.
      this.loading = false;

      const {
        data: { subscription },
      } = $supabase.auth.onAuthStateChange(async (event, newSession) => {
        if (event === 'SIGNED_IN' && newSession?.user) {
          this.user = newSession.user;
          // Keep your custom session key in sync
          localStorage.setItem('session', JSON.stringify(newSession));
          // This branch is the OAuth path: app.vue's restoreSession() already
          // ran and found nothing, because the PKCE exchange had not finished
          // yet. Without this fetch the store keeps only the raw auth user, so
          // the navbar renders logged-in with an empty userProfile and no
          // avatar until some page happens to select the profile itself.
          await this.fetchUserAndProfile(newSession);
          await this.checkAdmin();
          this.loading = false;
          subscription.unsubscribe();
        } else if (event === 'SIGNED_OUT') {
          this.user = { id: '', avatar_url: '', is_anonymous: false };
          this.isAdmin = false;
          this.avatarEnsuredFor = null;
          this.loading = false;
          subscription.unsubscribe();
        }
      });

      // Drop the late-arrival listener after a grace period so it does not
      // outlive the window it exists for. Unconditional: `loading` is already
      // false by now, so a guarded unsubscribe would never fire and every
      // initAuth() call would leak a subscription.
      setTimeout(() => {
        this.loading = false;
        subscription.unsubscribe();
      }, 5000);
    },
    async checkAdmin() {
      const { $supabase } = useNuxtApp();
      try {
        const { data, error } = await $supabase.functions.invoke('check-admin');
        if (error) throw error;
        this.isAdmin = data?.isAdmin ?? false;
      } catch (error) {
        console.error('Error checking admin status:', error);
        this.isAdmin = false;
      }
    },
    /**
     * Make sure the signed-in user has an avatar, generating one if not.
     *
     * Deliberately NOT awaited by fetchUserAndProfile. Generating means a
     * canvas encode, a storage upload and a profiles UPDATE; awaiting it there
     * put all of that on the critical path of the first paint (app.vue awaits
     * restoreSession before isAppReady), so a new user stared at a spinner
     * while their own avatar was being made. Setting userProfile is what the
     * UI needs; the avatar_url lands reactively a moment later.
     *
     * Idempotent and safe to call from anywhere — the plugin watcher fires it
     * on every auth path, and re-entrant calls share one run.
     */
    async ensureAvatar() {
      const profile = this.userProfile;
      if (!profile?.id) return null;
      if (this.avatarEnsuredFor === profile.id) return profile.avatar_url;
      // Only share the in-flight run when it is for THIS user (see above).
      if (avatarEnsureInFlight && avatarEnsureInFlightFor === profile.id) {
        return avatarEnsureInFlight;
      }

      if (profile.avatar_url) {
        // Nothing to do — record it so we don't re-check on every navigation.
        this.avatarEnsuredFor = profile.id;
        return profile.avatar_url;
      }

      const targetId = profile.id;
      const { generateAvatar } = useAvatar();

      const run = (async () => {
        try {
          // generateAvatar writes the new URL straight back into
          // userStore.userProfile.avatar_url, so the navbar updates itself.
          const url = await generateAvatar(profile);
          // generateAvatar swallows its own errors and returns null. Only mark
          // the id as settled on success, so a failed upload is retried on the
          // next navigation instead of being remembered as done.
          if (url) this.avatarEnsuredFor = targetId;
          return url;
        } finally {
          // Clear only if a later call for a different user has not already
          // taken the slot — otherwise the slow run clears the fresh one.
          if (avatarEnsureInFlight === run) {
            avatarEnsureInFlight = null;
            avatarEnsureInFlightFor = null;
          }
        }
      })();

      avatarEnsureInFlight = run;
      avatarEnsureInFlightFor = targetId;

      return run;
    },
    /**
     * Record where this account was created from, once.
     *
     * The write itself is `record-session-ip` — it CANNOT happen from here.
     * `profiles.ip_address` has no GRANT UPDATE to `authenticated` and is
     * pinned by protect_profile_privileged_columns, so a client-side write is a
     * hard 42501. This action's whole job is to ask the function to do it; see
     * that file for why a browser-supplied IP would be worse than none.
     *
     * Safe to call on every auth path: the function only writes when the column
     * is still NULL, so extra calls are a no-op server-side and accounts that
     * predate this get back-filled on their owner's next visit.
     *
     * Not awaited by callers — nothing on the render path should wait for it,
     * and there is nothing in the UI to update when it lands.
     */
    async ensureSessionIp() {
      const userId = this.userProfile?.id || this.user?.id;
      if (!userId) return;
      if (this.ipEnsuredFor === userId) return;

      // The client cannot READ ip_address either (it is not in the granted
      // column list), so there is no way to check locally whether this account
      // already has one. This flag is what stops a redundant call on every cold
      // start; losing it just means one extra no-op request.
      const flagKey = `sessionIpRecorded:${userId}`;
      try {
        if (localStorage.getItem(flagKey)) {
          this.ipEnsuredFor = userId;
          return;
        }
      } catch {
        // Private mode / storage disabled. Fall through and call: a wasted
        // round trip is better than never recording an origin at all.
      }

      // Claim the slot before awaiting so a burst (the plugin watcher plus a
      // direct call from fetchUserAndProfile) does one request, not two.
      this.ipEnsuredFor = userId;

      const { $supabase } = useNuxtApp();
      const { error } = await $supabase.functions.invoke('record-session-ip');

      if (error) {
        // Never fatal — this is telemetry, and a guest must still get their
        // session. But never silent either: a failure that logs nothing is how
        // the previous version of this feature stayed broken indefinitely.
        console.error('record-session-ip failed:', error.message);
        // Release the slot so the next navigation retries.
        this.ipEnsuredFor = null;
        return;
      }

      try {
        localStorage.setItem(flagKey, '1');
      } catch {
        // Nothing to do; the server-side NULL check is the real guard.
      }
    },
    async fetchUserAndProfile(supabaseSession = null) {
      this.isFetching = true;
      const { $supabase } = useNuxtApp();

      try {
        const session =
          supabaseSession || (await this.getSessionFromLocalStorage());

        if (!session || !session.user) {
          this.loading = false;
          this.isFetching = false;
          return;
        }

        const { data, error } = await $supabase.rpc(
          'get_current_user_with_profile'
        );

        if (error || !data || !data.profile) {
          // If this ever fires now, it's a real bug (trigger failed) —
          // not an expected "first login" case, so log it loudly.
          console.error('Profile missing unexpectedly:', error, data);
          this.loading = false;
          this.isFetching = false;
          return;
        }

        const { profile, ...authFields } = data;
        // The session is the authoritative source for is_anonymous — don't rely
        // on the RPC returning it, or guests would silently look like members.
        this.user = {
          ...authFields,
          is_anonymous: session.user?.is_anonymous === true,
        };
        this.userProfile = profile;
        // Not awaited on purpose — see ensureAvatar. The plugin watcher also
        // fires on userProfile.id, and ensureAvatar de-duplicates, so calling
        // here too costs nothing and covers callers that bypass the watcher.
        this.ensureAvatar();
        // Same reasoning, same de-duplication. This is the call that covers a
        // brand-new guest: continueAsGuest() lands here with the fresh session
        // before any watcher has seen the new profile id.
        this.ensureSessionIp();

        return this.userProfile;
      } catch (error) {
        console.error('Unexpected error during fetch:', error);
      } finally {
        this.loading = false;
        this.isFetching = false;
      }
    },
    /**
     * Tell already-mounted pages that this account's content has changed owner.
     *
     * Call it after a guest merge has finished — never before, or the refetch it
     * triggers races the rows it is meant to pick up.
     */
    markOwnedContentChanged() {
      this.ownedContentRevision++;
    },
    async resetUserStore() {
      this.user = { id: '', avatar_url: '', is_anonymous: false };
      this.userProfile = { id: '', avatar_url: '' };
      this.isAdmin = false;
      this.avatarEnsuredFor = null;
      this.ipEnsuredFor = null;
      this.loading = false;
    },
    async signOutUser() {
      this.loading = true;
      const { $supabase } = useNuxtApp();

      // A GUEST IS NOT SIGNED OUT — they are sent to the conversion screen.
      //
      // Signing a guest out for real destroys their work permanently: the
      // session token is the only handle on an anonymous account, so once it is
      // cleared nobody can ever authenticate as that user again. Doing that
      // silently, from a menu item, with no confirmation, is not a sign-out —
      // it is an unannounced delete.
      //
      // The previous behaviour was worse than either option: it skipped the
      // real signOut() AND localStorage.clear() but still wiped the store and
      // navigated away, so the UI showed a signed-out visitor while the guest
      // session was still sitting in storage, ready to reappear on the next
      // reload. Two sources of truth disagreeing about who you are.
      //
      // /login is the right destination because it already handles this case:
      // it detects the anonymous session and offers keep / merge / discard.
      if (this.user.is_anonymous) {
        this.loading = false;
        await navigateTo('/login');
        return;
      }

      const { error } = await $supabase.auth.signOut();
      if (error) {
        console.error('Error signing out:', error.message);
        this.loading = false;
        return;
      }
      localStorage.clear();

      this.user = { id: '', avatar_url: '', is_anonymous: false };
      this.userProfile = { id: '', avatar_url: '' };
      this.isAdmin = false;
      this.avatarEnsuredFor = null;
      await resetAllStores();

      // Navigate first, THEN clean up loading state
      await navigateTo('/login');
      this.loading = false;
    },
    /**
     * "Log out and discard my work" — delete a guest's content and their
     * anonymous account, then sign out for real.
     *
     * This is the one path that DOES sign a guest out, and it is only safe
     * because the work is deleted first: signOutUser() refuses to clear an
     * anonymous session precisely because the token is the only handle on it,
     * so a plain sign-out would leave the rows behind owned by a uid nobody can
     * ever authenticate as. Here they are already gone.
     *
     * Deleting is server-side work (`discard-guest`) — there is no DELETE
     * policy on `profiles`, so the browser cannot do it and, worse, is told it
     * succeeded. See the edge function's header.
     *
     * Returns `{ error }` rather than navigating: the two call sites land in
     * different places (the navbar on the landing page, the login page on its
     * own login tab).
     */
    async discardGuestSession() {
      const { $supabase } = useNuxtApp();

      // A member reaching this would lose their entire library. The edge
      // function rejects them too — this is the near guard, not the only one.
      if (!this.user.is_anonymous) {
        return { error: { message: 'This only applies to guest sessions.' } };
      }

      const { error } = await $supabase.functions.invoke('discard-guest');
      if (error) return { error };

      // The anonymous user no longer exists, so the stored session is worthless
      // and any error signing out is cosmetic by comparison.
      await $supabase.auth.signOut();
      localStorage.clear();

      this.user = { id: '', avatar_url: '', is_anonymous: false };
      this.userProfile = { id: '', avatar_url: '' };
      this.isAdmin = false;
      this.avatarEnsuredFor = null;
      await resetAllStores();

      return {};
    },
    async fetchUserProfile(user) {
      const { $supabase } = useNuxtApp(); // Access supabase client

      try {
        const { data, error } = await $supabase.functions.invoke(
          'get-user-profile',
          {
            body: JSON.stringify({ user: user }),
          }
        );
        if (error) {
          console.error('Error invoking function:', error);
          this.loading = false;
          return;
        }
        // Check if the response contains the expected profile data
        const profileData = data;
        if (!profileData || !profileData.profile) {
          console.error('Invalid profile data:', profileData);
          this.loading = false;
          return;
        }

        // Store user profile data
        this.userProfile = profileData.profile;
        this.ensureAvatar();
        this.loading = false;
      } catch (error) {
        console.error('Unexpected error during fetch:', error);
      } finally {
        this.loading = false;
      }
    },

    // Utility function for session retrieval
    async getSessionFromLocalStorage() {
      const { $supabase } = useNuxtApp();
      try {
        const {
          data: { session },
          error,
        } = await $supabase.auth.getSession();
        if (error) throw error;

        // Keep writing to 'session' key if anything else in your app reads it
        if (session) {
          localStorage.setItem('session', JSON.stringify(session));
        }

        return session;
      } catch (error) {
        console.error('Error fetching session:', error);
        return null;
      }
    },
  },
});
